/**
 * Pure state-shaping helpers used by `src/sentry.js`'s `beforeSend` hook.
 *
 * These were extracted from `sentry.js` so they can be unit-tested
 * directly — `sentry.js` itself is in `coveragePathIgnorePatterns`
 * because most of it is the side-effectful `Sentry.init` call, which is
 * hard to exercise in isolation. The pure transformations live here.
 */

export type ReduxContextKey = 'state' | 'redux.state';

export interface ReduxContextLookup {
  key: ReduxContextKey;
  value: unknown;
}

/**
 * Sentry's Redux enhancer (`@sentry/react` v8+) attaches store state under
 * `contexts.state` as `{ state: { type: 'redux', value } }`. Older code and
 * our previous `beforeSend` wrote to `contexts['redux.state']`. Prefer the
 * current key; fall back to the legacy one when present.
 */
export const getReduxContext = (
  contexts: Record<string, unknown> | null | undefined,
): ReduxContextLookup | null => {
  if (!contexts || typeof contexts !== 'object') return null;
  if (contexts.state != null) {
    return { key: 'state', value: contexts.state };
  }
  if (contexts['redux.state'] != null) {
    return { key: 'redux.state', value: contexts['redux.state'] };
  }
  return null;
};

/**
 * Filename `Sentry.createReduxEnhancer` uses when `attachReduxState` is
 * enabled. The enhancer's event processor copies the *raw* store state into
 * this attachment before `beforeSend` runs, so scrubbing `event.contexts`
 * alone does not keep PII out of Sentry.
 */
export const REDUX_STATE_ATTACHMENT_FILENAME = 'redux_state.json';

/**
 * Remove any raw Redux state attachment from a Sentry event hint, in place.
 * Defense-in-depth for `attachReduxState: false` on the enhancer. Never
 * throws.
 */
export const stripReduxStateAttachment = (
  hint: { attachments?: unknown } | null | undefined,
): void => {
  if (!hint || !Array.isArray(hint.attachments)) return;
  hint.attachments = hint.attachments.filter(
    attachment =>
      (attachment as { filename?: unknown } | null | undefined)?.filename !==
      REDUX_STATE_ATTACHMENT_FILENAME,
  );
};

const isV8ReduxContext = (
  value: unknown,
): value is { state: { type: 'redux'; value: unknown } } =>
  !!value &&
  typeof value === 'object' &&
  'state' in value &&
  !!(value as { state?: { type?: string } }).state &&
  typeof (value as { state: unknown }).state === 'object' &&
  (value as { state: { type?: string } }).state.type === 'redux';

const deepCloneJson = (value: unknown): unknown => {
  const serialized = JSON.stringify(value);
  if (serialized === undefined) return value;
  return JSON.parse(serialized);
};

/**
 * Clone and scrub a Redux context for Sentry. Never throws: unserializable
 * input becomes a truncation marker so `beforeSend` can still deliver the
 * original error event.
 */
export const sanitizeReduxContext = (
  reduxContext: unknown,
  keys: RegExp[],
): unknown => {
  if (reduxContext == null) return reduxContext;

  let cloned: unknown;
  try {
    cloned = deepCloneJson(reduxContext);
  } catch {
    const failure: SerializationFailureMarker = {
      sentryTruncated: true,
      sentryTruncationReason: 'redux_state_serialization_failed',
    };
    return failure;
  }

  if (cloned === reduxContext && typeof reduxContext !== 'object') {
    return cloned;
  }

  if (isV8ReduxContext(cloned)) {
    const storeState = cloned.state.value;
    if (storeState && typeof storeState === 'object') {
      const scrubbed = {
        ...(storeState as Record<string, unknown>),
        i18n: null,
      };
      sanitizeState(scrubbed, keys);
      cloned.state.value = truncateOversizedState(scrubbed);
    }
    return cloned;
  }

  if (cloned && typeof cloned === 'object' && !Array.isArray(cloned)) {
    const scrubbed = {
      ...(cloned as Record<string, unknown>),
      i18n: null,
    };
    sanitizeState(scrubbed, keys);
    return truncateOversizedState(scrubbed);
  }

  return truncateOversizedState(cloned);
};

// 125kB, as 200kB is the max payload size for Sentry events. The previous
// implementation used `normalizeToSize` from `@sentry/utils` which was
// removed from the public API in v8.
export const MAX_REDUX_STATE_SIZE = 125 * 1024;

// `normalizeToSize` worked by progressively reducing depth until the
// JSON-serialized payload fit. We replicate that behavior here so that
// oversized Redux state is trimmed-to-fit rather than replaced wholesale —
// preserving as much debug context as possible without losing the entire
// state on huge reducers.
//
// MAX_TRIM_DEPTH matches the explicit depth (12) the previous v6
// `normalizeToSize(event, 12, MAX_SIZE)` call passed in, which itself
// matched `normalizeDepth: 12` in the Sentry.init config. Anything
// shallower would unconditionally drop debug context at depths 9–12
// even when keeping it would still fit the size budget.
export const MIN_TRIM_DEPTH = 1;
export const MAX_TRIM_DEPTH = 12;
export const TRUNCATED_MARKER = '[Truncated]';

export interface SerializationFailureMarker {
  sentryTruncated: true;
  sentryTruncationReason:
    | 'redux_state_exceeded_max_size_at_min_depth'
    | 'redux_state_serialization_failed';
  sentryTruncationSizeBytes?: number;
  sentryTruncationMaxSizeBytes?: number;
}

export interface TrimmedStateMarker {
  sentryTrimmedToDepth: number;
  sentryOriginalSizeBytes: number;
}

/**
 * A finished Filestack upload (PickerFileMetadata): a candidate's document
 * with its filename, local path, storage key and CDN url. These land in
 * redux-form state and CHANGE breadcrumbs under arbitrary field names
 * (`idDocument`, `ssnDocument`, ...), so they are matched by shape, not key.
 */
const isFilestackFile = (value: unknown): boolean =>
  !!value &&
  typeof value === 'object' &&
  typeof (value as { filename?: unknown }).filename === 'string' &&
  ('handle' in value || 'uploadId' in value);

/**
 * Walks `state` in place and replaces the value of any key matching one
 * of `keys`, and any Filestack file object, with `null`. Recurses into
 * nested objects and arrays. Non-object/array primitives are left alone.
 */
export const sanitizeState = (state: unknown, keys: RegExp[]): void => {
  if (state === null || typeof state !== 'object') return;
  if (Array.isArray(state)) {
    state.forEach((item, index) => {
      if (isFilestackFile(item)) {
        state[index] = null;
        return;
      }
      sanitizeState(item, keys);
    });
    return;
  }
  const obj = state as Record<string, unknown>;
  Object.keys(obj).forEach(key => {
    if (keys.some(pattern => pattern.test(key)) || isFilestackFile(obj[key])) {
      obj[key] = null;
      return;
    }
    sanitizeState(obj[key], keys);
  });
};

/**
 * Recursively trim `value` so any descendant deeper than `maxDepth` is
 * replaced with `TRUNCATED_MARKER`. Mirrors the depth-reduction strategy
 * `@sentry/utils@6` used inside `normalizeToSize`.
 */
export const trimToDepth = (
  value: unknown,
  maxDepth: number,
  currentDepth = 0,
): unknown => {
  if (value === null || typeof value !== 'object') return value;
  if (currentDepth >= maxDepth) return TRUNCATED_MARKER;
  if (Array.isArray(value)) {
    return value.map(item => trimToDepth(item, maxDepth, currentDepth + 1));
  }
  const trimmed: Record<string, unknown> = {};
  Object.keys(value as Record<string, unknown>).forEach(key => {
    trimmed[key] = trimToDepth(
      (value as Record<string, unknown>)[key],
      maxDepth,
      currentDepth + 1,
    );
  });
  return trimmed;
};

/**
 * Walk the depth budget from `MAX_TRIM_DEPTH` down to `MIN_TRIM_DEPTH`
 * and return the deepest representation of `state` whose JSON payload
 * fits within `MAX_REDUX_STATE_SIZE`.
 *
 * - Returns `state` unchanged when it already fits.
 * - Returns a trimmed copy tagged with `sentryTrimmedToDepth` and
 *   `sentryOriginalSizeBytes` when trimming succeeds.
 * - Returns a `SerializationFailureMarker` if even depth `MIN_TRIM_DEPTH`
 *   doesn't fit, or if `JSON.stringify` throws (e.g. circular refs the
 *   caller didn't strip).
 */
export const truncateOversizedState = (state: unknown): unknown => {
  let serialized: string | undefined;
  try {
    serialized = JSON.stringify(state);
  } catch {
    const failure: SerializationFailureMarker = {
      sentryTruncated: true,
      sentryTruncationReason: 'redux_state_serialization_failed',
    };
    return failure;
  }

  if (serialized === undefined) {
    // `JSON.stringify` returns `undefined` for top-level `undefined`,
    // functions, or symbols. Nothing to trim — return the original.
    return state;
  }

  if (serialized.length <= MAX_REDUX_STATE_SIZE) return state;

  for (let depth = MAX_TRIM_DEPTH; depth >= MIN_TRIM_DEPTH; depth -= 1) {
    const trimmed = trimToDepth(state, depth);
    const trimmedSize = JSON.stringify(trimmed).length;
    if (trimmedSize <= MAX_REDUX_STATE_SIZE) {
      const stamped: Record<string, unknown> & TrimmedStateMarker = {
        ...(trimmed as Record<string, unknown>),
        sentryTrimmedToDepth: depth,
        sentryOriginalSizeBytes: serialized.length,
      };
      return stamped;
    }
  }

  const overBudget: SerializationFailureMarker = {
    sentryTruncated: true,
    sentryTruncationReason: 'redux_state_exceeded_max_size_at_min_depth',
    sentryTruncationSizeBytes: serialized.length,
    sentryTruncationMaxSizeBytes: MAX_REDUX_STATE_SIZE,
  };
  return overBudget;
};
